import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Tooltip,
  Avatar,
  Container, 
  Box, 
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme as useMuiTheme
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

const Navbar = ({ content }: any) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const muiTheme = useMuiTheme();
  const { user, isAuthenticated, logout } = useAuth();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    logout();
    handleCloseUserMenu();
    navigate('/');
  };

  const handleProfile = () => {
    handleCloseUserMenu();
    navigate('/profile');
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const userMenu = () => (
    <>
      <Tooltip title="Open settings">
        <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
          <Avatar
            alt={`${user?.firstName} ${user?.lastName}`}
            src={user?.imageUrl as string}
          />
        </IconButton>
      </Tooltip>
      {!isMobile && <Menu
        sx={{ mt: '45px' }}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
      >
        <MenuItem onClick={handleProfile}>
          <Typography textAlign="center">Profile</Typography>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <Typography textAlign="center">Logout</Typography>
        </MenuItem>
      </Menu>}
    </>
  );
  
  return (
    <AppBar position="static">
      <Container maxWidth="lg">
        <Toolbar>
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              flexGrow: 1,
            }}
          >
            <img
              src={content?.siteLogo}
              alt="Lummie's Femme Circle"
              style={{
                height: '40px',
                marginRight: '10px',
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {content?.siteName}
            </Typography>
          </Box>
          <IconButton 
            color="inherit" 
            onClick={toggleTheme}
            sx={{ ml: 1 }}
          >
            {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
          
          {isMobile ? (
            <>
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={open}
                onClose={handleClose}
              >
                {isAuthenticated && 
                  <MenuItem component={'div'}>
                    <IconButton onClick={(e) => {handleOpenUserMenu(e); handleClose();}} sx={{ p: 0 }}>
                      <Avatar
                        alt={`${user?.firstName} ${user?.lastName}`}
                        src={user?.imageUrl as string}
                      />
                    </IconButton>
                  </MenuItem>
                }
                <MenuItem component={RouterLink} to="/" onClick={handleClose}>
                  Home
                </MenuItem>
                <MenuItem component={RouterLink} to="/posts" onClick={handleClose}>
                  Blog
                </MenuItem>
                <MenuItem component={RouterLink} to="/categories" onClick={handleClose}>
                  Categories
                </MenuItem>
                <MenuItem component={RouterLink} to="/about" onClick={handleClose}>
                  About
                </MenuItem>
                {!isAuthenticated ?
                  <MenuItem component={RouterLink} to="/login" onClick={handleClose}>
                      Login
                  </MenuItem>
                  :
                  <>
                    <MenuItem onClick={() => {handleProfile(); handleClose();}}>
                      <Typography textAlign="center">Profile</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => {handleLogout(); handleClose()}}>
                      <Typography textAlign="center">Logout</Typography>
                    </MenuItem>
                  </>
                }
              </Menu>
            </>
          ) : (
            <>
              <Button color="inherit" component={RouterLink} to="/">
                Home
              </Button>
              <Button color="inherit" component={RouterLink} to="/posts">
                Blog
              </Button>
              <Button color="inherit" component={RouterLink} to="/categories">
                Categories
              </Button>
              <Button color="inherit" component={RouterLink} to="/about">
                About
              </Button>
              <Box sx={{ ml: 2 }}>
                {!isAuthenticated ?
                  <Button
                    component={RouterLink}
                    to="/login"
                    color="inherit"
                    variant="outlined"
                    sx={{ borderColor: 'white', '&:hover': { borderColor: 'white' } }}
                  >
                    Login
                  </Button>
                  : userMenu()
                }
              </Box>
            </>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar; 